import { useEffect, useMemo, useState } from 'react';
import {
  getThemeLabels,
  getThemePreference,
  getUserTheme,
  isDarkModeEnabled,
  isHamburgerThemeEnabled,
  isSettingsThemeEnabled,
  setUserTheme,
  subscribeUserTheme,
  USER_THEME_PREFERENCE_CHANGE_EVENT
} from 'Utils/theme';
import EnvironmentManager, { CMS_MESSAGES_READY_EVENT } from 'Utils/environmentManager';

export default function useUserTheme () {
  const [theme, setTheme] = useState(getUserTheme);
  const [preference, setPreference] = useState(getThemePreference);
  const [cmsTick, setCmsTick] = useState(0);

  useEffect(() => subscribeUserTheme(nextTheme => {
    setTheme(nextTheme);
    setPreference(getThemePreference());
  }), []);

  useEffect(() => {
    const onPreferenceChange = () => {
      setPreference(getThemePreference());
      setTheme(getUserTheme());
    };
    window.addEventListener(USER_THEME_PREFERENCE_CHANGE_EVENT, onPreferenceChange);
    return () => window.removeEventListener(USER_THEME_PREFERENCE_CHANGE_EVENT, onPreferenceChange);
  }, []);

  useEffect(() => {
    const onMessagesReady = () => setCmsTick(tick => tick + 1);
    window.addEventListener(CMS_MESSAGES_READY_EVENT, onMessagesReady);
    if (EnvironmentManager.isLiveMessagesReady()) {
      onMessagesReady();
    }
    return () => window.removeEventListener(CMS_MESSAGES_READY_EVENT, onMessagesReady);
  }, []);

  const changeTheme = nextTheme => {
    const applied = setUserTheme(nextTheme);
    setTheme(applied);
    setPreference(getThemePreference());
    return applied;
  };

  const cms = useMemo(() => ({
    labels: getThemeLabels(),
    isDarkModeEnabled: isDarkModeEnabled(),
    isHamburgerThemeEnabled: isHamburgerThemeEnabled(),
    isSettingsThemeEnabled: isSettingsThemeEnabled()
  }), [cmsTick]);

  return [theme, changeTheme, cms, preference];
}
